import {ChangeDetectorRef, Component, EventEmitter, Input, Output} from '@angular/core';
import {SystemRole} from "../../../../../_models/user";
import {NotificationSettingsService} from "../../../../../_services/notification-settings.service";
import {EmailSettings, NotificationSettings, SettingItem} from "../../../../../_models/notificationSettings";
import {modalAnimation} from "../../../../../config/constants";
import {NotificationService} from "../../../../../_services/notification.service";
import {Destroyable} from "../../../../../config/destroyable";
import {takeUntil} from "rxjs";

@Component({
    selector: 'app-email-notification-settings',
    templateUrl: './email-notification-settings.component.html',
    styleUrls: ['./email-notification-settings.component.scss'],
    animations: [modalAnimation],
    standalone: false
})
export class EmailNotificationSettingsComponent extends Destroyable{
  @Output() close: EventEmitter<any> = new EventEmitter<any>()

  submitBtnDisabled = false;

  @Input()
  set currentUserRole(value: SystemRole) {
    if (value === SystemRole.ROLE_COMPANY_ADMIN) {
      this.settings.all.innerToggles.colorBoards.visible = false;
      this.settings.all.innerToggles.projects.visible = false;
    } else if (value === SystemRole.ROLE_TRADE) {
      this.settings.all.innerToggles.colorBoards.visible = false;
      this.settings.all.innerToggles.noteComments.visible = false;
    }
  }

  settings: EmailSettings = {
    all: {
      toggled: true,
      visible: true,

      innerToggles: {
        colorBoards: {
          toggled: true,
          visible: true
        },
        projects: {
          toggled: true,
          visible: true
        },
        areaComments: {
          toggled: true,
          visible: true
        },
        noteComments: {
          toggled: true,
          visible: true
        },
        elementComments: {
          toggled: true,
          visible: true
        },
        tasks: {
          toggled: true,
          visible: true,
          innerToggles: {
            newTask: {
              toggled: true,
              visible: true
            },
            completedTasks: {
              toggled: true,
              visible: true
            },
            taskReminder: {
              toggled: true,
              visible: true,
              period: 3
            },
            taskComments: {
              toggled: true,
              visible: true
            }
          }
        }
      }
    }
  }

  mainSettings: SettingItem[] = [
    {
      key: 'colorBoards',
      label: 'Color Boards',
      description: 'When you are added to a new color board',
      checkVisibility: true
    },
    {
      key: 'projects',
      label: 'Projects',
      description: 'When you are added to a new project',
      checkVisibility: true
    },
    {
      key: 'elementComments',
      label: null,
      isGroup: true
    },
    {
      key: 'noteComments',
      label: 'Note Comments',
      description: 'When you are mentioned in a note comment'
    },
    {
      key: 'tasks',
      label: 'Tasks',
      isGroup: true
    }
  ];

  taskSubSettings: SettingItem[] = [
    {
      key: 'newTask',
      label: 'New Tasks',
      description: 'When you are assigned a new task'
    },
    {
      key: 'completedTasks',
      label: 'Completed Task',
      description: 'When a task you created has been completed'
    },
    {
      key: 'taskReminder',
      label: 'Task Reminder',
      description: 'Daily reminder to complete task'
    },
    {
      key: 'taskComments',
      label: 'Task Comments',
      description: 'When you are mentioned in a task comment'
    }
  ];

  notificationSettings = new NotificationSettings()
  taskReminderMap: Map<number, string> = new Map<number, string>([
    [1, "1 day"],
    [2, "2 days"],
    [3, "3 days"],
  ])

  constructor(private notificationSettingsService: NotificationSettingsService,
              private notifService: NotificationService,
              private cdRef: ChangeDetectorRef) {
    super();
  }

  ngOnInit() {
    this.notificationSettingsService.getNotificationSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
      this.notificationSettings = data;
      this.initTreeFromModel(this.notificationSettings);
    });
  }

  submit() {
    this.notificationSettingsService.updateNotificationSettings(this.notificationSettings)
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
      this.notifService.successNotification()
      this.close.emit()
    })
  }

  toggleSetting(path: string[]) {
    // 1. Find our node
    const node = this.getNodeByPath(path);
    const newValue = !node.toggled;
    node.toggled = newValue;

    // 2. Propagate this value down the entire subtree
    this.propagateDown(node, newValue);

    // 3. Push changes up, starting from the deepest level parent
    for (let depth = path.length - 1; depth >= 1; depth--) {
      const parentPath = path.slice(0, depth);
      const parent = this.getNodeByPath(parentPath);

      const visibleChildren = Object.values(parent.innerToggles || {})
        .filter((ch: any) => ch.visible);

      if (visibleChildren.length === 0) {
        // if there are no visible children, skip the node
        continue;
      }

      const someTrue = visibleChildren.some((ch: any) => ch.toggled);
      const allTrue  = visibleChildren.every((ch: any) => ch.toggled);

      // for root ‘all’ — AND, for the rest — OR
      parent.toggled = parentPath.join('.') === 'all'
        ? allTrue
        : someTrue;
    }

    this.syncSettings();
  }

  /** Finds a node by an array of keys, e.g. ['all','tasks','newTask'] */
  getNodeByPath(path: string[]): any {
    let current: any = this.settings;
    for (const key of path) {
      // first direct property, then innerToggles
      current = current[key] ?? current.innerToggles?.[key];
      if (!current) {
        throw new Error(`Invalid path: ${path.join('.')}`);
      }
    }
    return current;
  }

  /** Recursively sets all descendants of node.toggled = value */
  propagateDown(node: any, value: boolean): void {
    if (!node.innerToggles) return;
    for (const child of Object.values(node.innerToggles)) {
      // @ts-ignore
      if (!child.visible) {
        // skip invisible nodes and, accordingly, their entire subtree
        continue;
      }
      // @ts-ignore
      child.toggled = value;
      this.propagateDown(child, value);
    }
  }

  syncSettings() {
    this.notificationSettings.colorBoards = this.settings.all.innerToggles.colorBoards.toggled;
    this.notificationSettings.projects = this.settings.all.innerToggles.projects.toggled;
    this.notificationSettings.elementComments = this.settings.all.innerToggles.elementComments.toggled;
    this.notificationSettings.noteComments = this.settings.all.innerToggles.noteComments.toggled;
    this.notificationSettings.newTask = this.settings.all.innerToggles.tasks.innerToggles.newTask.toggled;
    this.notificationSettings.completedTask = this.settings.all.innerToggles.tasks.innerToggles.completedTasks.toggled;
    this.notificationSettings.taskReminder = this.settings.all.innerToggles.tasks.innerToggles.taskReminder.toggled;
    this.notificationSettings.taskReminderPeriod = this.settings.all.innerToggles.tasks.innerToggles.taskReminder.period;
    this.notificationSettings.taskComments = this.settings.all.innerToggles.tasks.innerToggles.taskComments.toggled;
  }

  initTreeFromModel(model: NotificationSettings) {
    const root = this.settings.all.innerToggles;

    root.colorBoards.toggled  = model.colorBoards;
    root.projects.toggled     = model.projects;
    root.noteComments.toggled = model.noteComments;
    root.elementComments.toggled = model.elementComments;

    const tasks = root.tasks.innerToggles;
    tasks.newTask.toggled        = model.newTask;
    tasks.completedTasks.toggled  = model.completedTask;
    tasks.taskReminder.toggled    = model.taskReminder;

    root.tasks.innerToggles.taskReminder.period = model.taskReminderPeriod;
    tasks.taskComments.toggled     = model.taskComments;

    this.syncAll();
  }

  syncAll() {
    const tasksNode = this.settings.all.innerToggles.tasks;
    const taskChildren = Object.values(tasksNode.innerToggles)
      .filter((ch: any) => ch.visible);
    tasksNode.toggled = taskChildren.some((ch: any) => ch.toggled);

    const topChildren = Object.values(this.settings.all.innerToggles)
      .filter((ch: any) => ch.visible);
    this.settings.all.toggled = topChildren.every((ch: any) => ch.toggled);

    this.cdRef.markForCheck();
  }

  protected readonly SystemRole = SystemRole;
}
